import validator from '@/constant/valitator';
import postLoginByEmailConfirm from '@/fetches/user/auth/post-login-by-email-confirm';
import useAlert from '@/hooks/use-alert';
import useSelectorUser from '@/hooks/use-selector-user';
import { actions } from '@/store';
import { EAuthModalPage } from '@/store/types';
import theme from '@/styles/theme';
import React, { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import Message from '@/constant/message';
import { AnchorText } from '../common/anchor-text';
import { AuthButton } from '../common/auth-button';
import { AuthTitle } from '../common/auth-title';
import { InputText } from '../common/input-text';

const ButtonsWrap = styled.div`
  display: flex;

  button {
    flex: 1;
  }
  button:last-child {
    background-color: ${theme.color.gray};
    color: ${theme.color.white};
  }

  button + button {
    margin-left: 15px;
  }
`;

/** (비밀번호 없을경우) 새 비밀번호 입력 */
export const InputNewPassword: React.FC = () => {
  const { email } = useSelectorUser('authModal');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [nameError, setNameError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [confirmPasswordError, setConfirmPasswordError] = useState('');
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { alert } = useAlert();

  const validateName = useCallback((value: string) => {
    const { err, args } = validator.checkName(value);
    if (err) {
      return t(err, args);
    }
    return '';
  }, [t]);

  const validatePassword = useCallback((value: string) => {
    const { err, args } = validator.checkPassword(value);
    if (err) {
      if (args?.value) {
        return t(err, { value: args.value }) as string;
      }
      if (args?.valueKeys) {
        return t(err, { value: args.valueKeys.map((v) => t(v)).join('\n') }) as string;
      }
      return t(err) as string;
    }
    return '';
  }, [t]);

  const validateConfirmPassword = useCallback((value: string) => {
    if (!value) {
      return t(Message.비밀번호확인란을입력해주세요);
    } if (password !== value) {
      return t(Message.입력한두비밀번호가다릅니다);
    }
    return '';
  }, [password, t]);

  const onChangeNameInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.currentTarget;
    setName(value);
    setNameError(validateName(value));
  }, [validateName]);

  const onChangePasswordInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.currentTarget;
    setPassword(value);
    setPasswordError(validatePassword(value));
  }, [validatePassword]);

  const onChangeConfirmPasswordInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.currentTarget;
    setConfirmPassword(value);
    setConfirmPasswordError(t(validateConfirmPassword(value)));
  }, [validateConfirmPassword, t]);

  const submit = useCallback((skip: boolean) => async () => {
    if (!skip) {
      let err = '';
      if (!err) err = validateName(name);
      if (!err) err = validatePassword(password);
      if (!err) err = validateConfirmPassword(confirmPassword);

      if (err) {
        await alert(err);
        return;
      }
    }
    setLoading(true);
    const { data, error, deletedAt } = await postLoginByEmailConfirm({
      email,
      name: skip ? undefined : name,
      password: skip ? undefined : password,
    });

    if (data) {
      dispatch(actions.user.authModal.setToken(data));
      dispatch(actions.user.authModal.setCurrentPage(EAuthModalPage.SUCCESS));
    } else if (error === 'require confirm email') {
      alert(t(Message.인증절차가제대로진행되지않았습니다다시시도해주세요));
    } else if (error === 'member is pending deletion') {
      dispatch(actions.user.authModal.setDeletedAt(deletedAt));
      dispatch(actions.user.authModal.setCurrentPage(EAuthModalPage.WILL_BE_DELETED_ACCOUNT));
    } else if (error === 'internal server error') {
      alert(t(Message.처리도중서버에서오류가발생했습니다잠시후다시시도하시거나관리자에게문의해주세요));
    } else {
      alert(t(Message.처리도중서버에서오류가발생했습니다잠시후다시시도하시거나관리자에게문의해주세요));
    }
    setLoading(false);
  }, [alert, confirmPassword, dispatch, email,
    name, password, validateConfirmPassword, validateName, validatePassword, t]);

  return (
    <>
      <AuthTitle>{t(Message.추가정보입력)}</AuthTitle>
      <AnchorText>
        {t(Message.STEAMuP에서사용할이름과새로운비밀번호를입력하세요)}
        <br />
        {t(Message.비밀번호를입력해두면다음에로그인이편리합니다)}
      </AnchorText>
      <InputText
        type="text"
        title={t(Message.이름)}
        placeholder={t(Message.이름)}
        errorText={nameError}
        value={name}
        onChange={onChangeNameInput}
        disabled={loading}
        autoFocus
        autoComplete="off"
      />
      <InputText
        type="password"
        title={t(Message.비밀번호)}
        placeholder={t(Message.비밀번호)}
        errorText={passwordError}
        value={password}
        onChange={onChangePasswordInput}
        disabled={loading}
        autoComplete="new-password"
      />
      <InputText
        type="password"
        title={t(Message.비밀번호확인)}
        placeholder={t(Message.비밀번호확인)}
        errorText={confirmPasswordError}
        value={confirmPassword}
        onChange={onChangeConfirmPasswordInput}
        disabled={loading}
        autoComplete="new-password"
      />
      <ButtonsWrap>
        <AuthButton type="button" disabled={loading} onClick={submit(false)}>{t(Message.확인)}</AuthButton>
        <AuthButton type="button" disabled={loading} onClick={submit(true)}>{t(Message.다음에설정하기)}</AuthButton>
      </ButtonsWrap>
    </>
  );
};
